import AppShell from "@/components/AppShell";
import NewTaskCard from "@/components/NewTaskCard";
import DeleteConfirm from "@/components/DeleteConfirm";
import DetailsPanel from "@/components/DetailsPanel";
import { buildListOptions } from "@/lib/listOptions";
import EmptyState from "@/components/EmptyState";
import LotusIcon from "@/components/LotusIcon";
import MyDayBlockHeader, { type MyDayBlockDropData } from "@/components/MyDayBlockHeader";
import MyDayPlanModal from "@/components/MyDayPlanModal";
import PageHeader from "@/components/PageHeader";
import SearchLink from "@/components/SearchLink";
import SparkleIcon from "@/components/SparkleIcon";
import TaskContextMenu, { type MenuPatch } from "@/components/TaskContextMenu";
import TaskRow, { LEAVING_ROW_CLASSES, type TaskData } from "@/components/TaskRow";
import { blockListSortingStrategy } from "@/lib/blockSorting";
import { classifyReorder, neighborsForReorder, promotedSubtask } from "@/lib/reorder";
import { useMyDayPlanStore } from "@/stores/myDayPlan";
import {
  createTask,
  toggleTask as mutateToggle,
  deleteTask as mutateDelete,
  updateTask,
  reorderTask,
  reorderMyDayTask,
  bulkPatchTasks,
  bulkDeleteTasks,
} from "@/mutations/tasks";
import { useTaskSelection } from "@/hooks/useTaskSelection";
import { useLeavingRows } from "@/hooks/useLeavingRows";
import { useTimeGate } from "@/hooks/useTimeGate";
import HiddenTasksToggle from "@/components/HiddenTasksToggle";
import { taskCount } from "@/lib/plural";
import type { SharedProps } from "@/types";
import { consumeTaskDeepLink } from "@/lib/taskLink";
import { useDndMonitor, type DragEndEvent, type DragStartEvent } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Link, usePage } from "@inertiajs/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PressPoint } from "@/hooks/useLongPress";
import { useCompletedSection } from "@/hooks/useCompletedSection";
import { useLocalView } from "@/hooks/useLocalView";
import { flushOutbox } from "@/lib/syncEngine";
import { isOfflineStandIn, OFFLINE_EMPTY_TEXT } from "@/lib/swClient";
import { myDay, subtasks, type ViewSource } from "@/lib/views";
import { NEEDS_NETWORK, useOnline } from "@/hooks/useOnline";

interface Props {
  pendingTasks: TaskData[];
  completedTasksExist: boolean;
  completedTasks?: TaskData[] | null;
  inboxListId?: string;
  strategy?: string;
  backlogNonEmpty: boolean;
  fetchedAt?: string;
}

const SUN_EMPTY = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

interface LocalMyDay {
  pending: TaskData[];
  completed: TaskData[];
  backlogNonEmpty: boolean;
  inboxListId: string | null;
}

function localMyDay(source: ViewSource): LocalMyDay {
  const inbox = source.lists.find((l) => l.kind === "inbox" && !l.discardedAt);
  return { ...(myDay(source) as Omit<LocalMyDay, "inboxListId">), inboxListId: inbox?.id ?? null };
}

// The strategy is a user setting the local store does not hold; a page rendered
// without it keeps the one this session saw last.
let lastKnownStrategy = "";

export default function MyDayShow(props: Props) {
  const online = useOnline();
  const local = useLocalView("myDay", localMyDay);
  const pendingTasks = local?.pending ?? props.pendingTasks;
  const completedTasksExist = local ? local.completed.length > 0 : props.completedTasksExist;
  const backlogNonEmpty = local?.backlogNonEmpty ?? props.backlogNonEmpty;
  if (props.strategy !== undefined) lastKnownStrategy = props.strategy;
  const strategy = props.strategy ?? lastKnownStrategy;
  const sidebar = usePage<SharedProps>().props.sidebar;
  const inboxId = sidebar?.inbox?.id ?? local?.inboxListId ?? props.inboxListId ?? null;
  const [selectedTask, setSelectedTask] = useState<TaskData | null>(null);
  // A dragged order holds only over the rows it was made from: once they change, they win.
  const [draggedOrder, setDraggedOrder] = useState<{ from: TaskData[]; order: TaskData[] } | null>(null);
  const orderedPending = draggedOrder?.from === pendingTasks ? draggedOrder.order : pendingTasks;
  const [taskPendingDelete, setTaskPendingDelete] = useState<TaskData | null>(null);
  const completed = useCompletedSection(props.completedTasks, local?.completed ?? null);
  const cachedCompleted = completed.cached;
  const [newTitle, setNewTitle] = useState("");
  const [newDueOn, setNewDueOn] = useState<string | null>(null);
  const [newRecurrence, setNewRecurrence] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Subtrees fetched from the server while the local copy is not ready yet.
  const [childrenByParent, setChildrenByParent] = useState<Record<string, TaskData[]>>({});
  const expandedParentIds = useMemo(() => [...expandedIds], [expandedIds]);
  const selectChildren = useCallback((source: ViewSource) => subtasks(source, expandedParentIds), [expandedParentIds]);
  const localChildren = useLocalView("myDay", selectChildren) as Record<string, TaskData[]> | null;
  const children = useMemo(
    () => localChildren ?? childrenByParent,
    [localChildren, childrenByParent],
  );
  const [contextMenu, setContextMenu] = useState<{ tasks: TaskData[]; x: number; y: number } | null>(null);
  const [bulkDeletePending, setBulkDeletePending] = useState<TaskData[] | null>(null);
  const [rootDragActive, setRootDragActive] = useState(false);
  const [collapsedBlocks, setCollapsedBlocks] = useState({ anyTime: false, inOrder: false });
  const fetchingRef = useRef<Set<string>>(new Set());

  function patchTaskFromMenu(task: TaskData, payload: MenuPatch) {
    updateTask(task.id, payload, { onSuccess: refreshExpandedSubtrees });
  }

  function submitNewTask(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = newTitle.trim();
    if (!trimmed || !inboxId) return;
    createTask({
      title: trimmed,
      listId: inboxId,
      myDay: true,
      dueOn: newDueOn ?? null,
      recurrenceRule: newRecurrence ?? null,
    });
    setNewTitle("");
    setNewDueOn(null);
    setNewRecurrence(null);
  }

  const listOptions = buildListOptions(sidebar);

  // Only the My Day widget links here, and it lists top-level My Day tasks — unlike
  // Lists/Show, no fetch fallback is needed.
  useEffect(() => {
    const taskId = consumeTaskDeepLink();
    const found = taskId && pendingTasks.find((t) => t.id === taskId);
    if (found) setSelectedTask(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedTask) return;
    // Subtasks aren't in the My Day prop set; let DetailsPanel manage them.
    if ((selectedTask.parentId ?? null) !== null) return;
    const fresh = [...pendingTasks, ...(cachedCompleted ?? [])].find((t) => t.id === selectedTask.id);
    if (!fresh) setSelectedTask(null);
    else if (fresh !== selectedTask) setSelectedTask(fresh);
  }, [pendingTasks, cachedCompleted, selectedTask]);

  // A subtask dropped on a list becomes a root there (Sidebar fires the request). It has
  // to leave the parent's row here and now: subtasks live in this component's own cache,
  // which the local copy does not reach before it is ready.
  function dropFromSubtree({ taskId, parentId }: { taskId: string; parentId: string }) {
    if (localChildren) return;
    setChildrenByParent((prev) => {
      const siblings = prev[parentId];
      if (!siblings) return prev;
      return { ...prev, [parentId]: siblings.filter((c) => c.id !== taskId) };
    });
  }

  // Where the task lands: next to the row it was dropped on, "first" in the block (dropped on
  // its header) or "last" (the context menu's move between blocks). Neighbours come from the
  // target block only: the server threads the key among that block's rows.
  function moveRootTask(activeId: string, at: { overId: string } | "first" | "last", sequential: boolean) {
    const board = [
      ...orderedPending.filter((t) => !t.myDaySequential),
      ...orderedPending.filter((t) => t.myDaySequential),
    ];
    const oldIndex = board.findIndex((t) => t.id === activeId);
    if (oldIndex === -1) return;
    const moved = { ...board[oldIndex], myDaySequential: sequential };

    let reordered: TaskData[];
    if (at === "first" || at === "last") {
      const rest = board.filter((t) => t.id !== activeId);
      const anyTimeCount = rest.filter((t) => !t.myDaySequential).length;
      const index = sequential
        ? at === "first" ? anyTimeCount : rest.length
        : at === "first" ? 0 : anyTimeCount;
      reordered = [...rest.slice(0, index), moved, ...rest.slice(index)];
    } else {
      const newIndex = board.findIndex((t) => t.id === at.overId);
      if (newIndex === -1) return;
      reordered = arrayMove(board, oldIndex, newIndex).map((t) => (t.id === activeId ? moved : t));
    }

    setDraggedOrder({ from: pendingTasks, order: reordered });
    const block = reordered.filter((t) => !!t.myDaySequential === sequential);
    reorderMyDayTask(activeId, { ...neighborsForReorder(block, activeId), sequential }, {
      onError: () => setDraggedOrder(null),
    });
  }

  useDndMonitor({
    onDragStart: (e: DragStartEvent) => {
      const data = e.active.data.current as { kind?: string; parentId?: string | null } | undefined;
      setRootDragActive(data?.kind === "task" && (data.parentId ?? null) === null);
    },
    onDragCancel: () => setRootDragActive(false),
    onDragEnd: (e: DragEndEvent) => {
      setRootDragActive(false);
      const { active, over } = e;
      const activeData = active.data.current as
        | { kind?: string; taskId?: string; parentId?: string | null }
        | undefined;
      const overData = over?.data.current as
        | { kind?: string; parentId?: string | null }
        | undefined;

      const promoted = promotedSubtask(activeData, overData);
      if (promoted) {
        dropFromSubtree(promoted);
        return;
      }

      if (activeData?.kind === "task" && (activeData.parentId ?? null) === null && overData?.kind === "myDayBlock") {
        moveRootTask(String(active.id), "first", (overData as unknown as MyDayBlockDropData).sequential);
        return;
      }

      if (activeData?.kind !== "task" || overData?.kind !== "task") return;
      if (!over || active.id === over.id) return;

      const target = classifyReorder(
        activeData.parentId ?? null,
        overData.parentId ?? null,
      );
      if (target.kind === "noop") return;

      if (target.kind === "root") {
        const overTask = orderedPending.find((t) => t.id === over.id);
        if (overTask) moveRootTask(String(active.id), { overId: overTask.id }, !!overTask.myDaySequential);
        return;
      }

      const parentId = target.parentId;
      const group = children[parentId] ?? [];
      const oldIndex = group.findIndex((t) => t.id === active.id);
      const newIndex = group.findIndex((t) => t.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(group, oldIndex, newIndex);
      const neighbors = neighborsForReorder(reordered, String(active.id));
      if (localChildren) {
        reorderTask(String(active.id), neighbors);
        return;
      }
      setChildrenByParent((prev) => ({ ...prev, [parentId]: reordered }));
      reorderTask(String(active.id), neighbors, {
        onError: () => setChildrenByParent((prev) => ({ ...prev, [parentId]: group })),
      });
    },
  });

  async function fetchChildren(parentId: string) {
    if (fetchingRef.current.has(parentId)) return;
    fetchingRef.current.add(parentId);
    try {
      await flushOutbox();
      const res = await fetch(`/tasks/${parentId}`, { headers: { Accept: "application/json" } });
      if (!res.ok) return;
      const data = await res.json();
      setChildrenByParent((prev) => ({ ...prev, [parentId]: data.subtasks ?? [] }));
    } finally {
      fetchingRef.current.delete(parentId);
    }
  }

  function refreshExpandedSubtrees() {
    if (localChildren) return Promise.resolve();
    return Promise.all([...expandedIds].map(fetchChildren));
  }

  function toggleExpand(task: TaskData) {
    const id = task.id;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (!localChildren && !childrenByParent[id]) void fetchChildren(id);
      }
      return next;
    });
  }

  function toggleTask(task: TaskData) {
    mutateToggle(task, { onSuccess: refreshExpandedSubtrees });
  }

  function renderTaskTree(task: TaskData, depth: number, dndEnabled: boolean): React.ReactNode {
    const isExpanded = expandedIds.has(task.id);
    const shownChildren = visibleChildren(task.id);
    return (
      <div key={task.id} className={task.leaving ? LEAVING_ROW_CLASSES : undefined}>
        <TaskRow
          task={task}
          onToggle={toggleTask}
          onRequestDelete={setTaskPendingDelete}
          onDelete={deleteTaskNow}
          onSelect={selectRow}
          onToggleExpand={toggleExpand}
          onContextMenu={openContextMenu}
          expanded={isExpanded}
          depth={depth}
          draggable={dndEnabled && task.status === "pending" && !task.leaving}
          hideMyDayBadge
          selected={selection.isSelected(task.id)}
          dragGroupIds={selection.dragGroupFor(task.id)}
        />
        {isExpanded && shownChildren.length > 0 && (
          <SortableContext
            items={shownChildren.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <div>
              {shownChildren.map((child) => renderTaskTree(child, depth + 1, dndEnabled))}
            </div>
          </SortableContext>
        )}
      </div>
    );
  }

  function deleteTaskNow(task: TaskData) {
    const deletedId = task.id;
    const parentId = task.parentId ?? null;
    mutateDelete(deletedId);
    setExpandedIds((prev) => {
      if (!prev.has(deletedId)) return prev;
      const next = new Set(prev);
      next.delete(deletedId);
      return next;
    });
    setChildrenByParent((prev) => {
      const { [deletedId]: _drop, ...rest } = prev;
      if (parentId && rest[parentId]) {
        rest[parentId] = rest[parentId].filter((c) => c.id !== deletedId);
      }
      return rest;
    });
  }

  function confirmDeleteTask() {
    if (!taskPendingDelete) return;
    deleteTaskNow(taskPendingDelete);
    setTaskPendingDelete(null);
  }

  const visibleChildren = useCallback((parentId: string): TaskData[] => children[parentId] ?? [], [children]);

  const pendingRows = useLeavingRows(orderedPending);
  const { shown: visiblePending, hiddenCount, revealed, toggleRevealed } = useTimeGate(pendingRows);
  const { anyTimeRows, inOrderRows, boardRows } = useMemo(() => {
    const anyTime = visiblePending.filter((t) => !t.myDaySequential);
    const inOrder = visiblePending.filter((t) => t.myDaySequential);
    const shownAnyTime = inOrder.length > 0 && collapsedBlocks.anyTime ? [] : anyTime;
    const shownInOrder = collapsedBlocks.inOrder ? [] : inOrder;
    return { anyTimeRows: anyTime, inOrderRows: inOrder, boardRows: [...shownAnyTime, ...shownInOrder] };
  }, [visiblePending, collapsedBlocks]);
  const hasInOrder = inOrderRows.length > 0;
  const shownAnyTimeRows = boardRows.filter((t) => !t.myDaySequential);
  const shownInOrderRows = boardRows.filter((t) => t.myDaySequential);
  const boardIds = useMemo(() => boardRows.map((t) => t.id), [boardRows]);
  const blockBoundary = shownAnyTimeRows.length;
  const boardStrategy = useMemo(() => blockListSortingStrategy(blockBoundary), [blockBoundary]);
  const toggleBlock = (block: keyof typeof collapsedBlocks) =>
    setCollapsedBlocks((prev) => ({ ...prev, [block]: !prev[block] }));
  // See the note in Lists/Show: an empty active part shows its empty state even when
  // something sits in Completed.
  const allDone = visiblePending.length === 0 && hiddenCount === 0 && completedTasksExist;

  // Flat rendered order (roots + expanded children) for shift-range + id→task lookup.
  const { visibleOrder, taskById } = useMemo(() => {
    const order: string[] = [];
    const byId = new Map<string, TaskData>();
    const walk = (t: TaskData) => {
      order.push(t.id);
      byId.set(t.id, t);
      if (expandedIds.has(t.id)) {
        for (const c of visibleChildren(t.id)) walk(c);
      }
    };
    boardRows.forEach(walk);
    return { visibleOrder: order, taskById: byId };
  }, [boardRows, expandedIds, visibleChildren]);

  const selection = useTaskSelection(visibleOrder);

  function openContextMenu(point: PressPoint, task: TaskData) {
    const ids = selection.selectForContext(task.id);
    const picked = ids.map((id) => taskById.get(id)).filter((t): t is TaskData => !!t);
    setContextMenu({ tasks: picked.length ? picked : [task], x: point.clientX, y: point.clientY });
  }

  function selectRow(task: TaskData, e: React.MouseEvent) {
    const intent = selection.handleClick(task.id, e);
    setSelectedTask(intent === "open" ? task : null);
  }

  function applyMenuPatch(payload: MenuPatch) {
    if (!contextMenu) return;
    const ids = contextMenu.tasks.map((t) => t.id);
    if (ids.length === 1) {
      patchTaskFromMenu(contextMenu.tasks[0], payload);
    } else {
      bulkPatchTasks(ids, payload, { onSuccess: refreshExpandedSubtrees });
    }
    selection.clear();
  }

  function confirmBulkDelete() {
    if (!bulkDeletePending) return;
    const ids = bulkDeletePending.map((t) => t.id);
    bulkDeleteTasks(ids, { onSuccess: refreshExpandedSubtrees });
    setExpandedIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of ids) if (next.delete(id)) changed = true;
      return changed ? next : prev;
    });
    setChildrenByParent((prev) => {
      const rest: Record<string, TaskData[]> = {};
      for (const [pid, kids] of Object.entries(prev)) {
        if (ids.includes(pid)) continue;
        rest[pid] = kids.filter((c) => !ids.includes(c.id));
      }
      return rest;
    });
    selection.clear();
    setBulkDeletePending(null);
  }

  return (
    <AppShell>
      <div className="flex flex-col min-h-dvh">
      <PageHeader
        title="Мой день"
        titleAfter={
          <>
            <Link
              href="/zen?my_day=1"
              aria-label="Дзен-режим"
              title="Дзен-режим"
              className="ml-2 text-gray-400 hover:text-blue-500 dark:text-gray-500"
            >
              <LotusIcon className="w-5 h-5 md:w-6 md:h-6" />
            </Link>
            <button
              type="button"
              title={online ? "Спланировать день" : NEEDS_NETWORK}
              aria-label="Спланировать день"
              disabled={!online || (orderedPending.length === 0 && !backlogNonEmpty)}
              onClick={() =>
                useMyDayPlanStore.getState().openFor(
                  { currentMyDayIds: orderedPending.map((t) => t.id) },
                  strategy,
                )
              }
              className="text-gray-400 hover:text-blue-500 dark:text-gray-500 cursor-pointer disabled:opacity-40 disabled:cursor-default p-1"
            >
              <SparkleIcon className="w-5 h-5" />
            </button>
          </>
        }
        actions={<SearchLink from="my_day" />}
      />

      {inboxId && (
        <div className="px-5 md:px-7">
          <form onSubmit={submitNewTask}>
            <NewTaskCard
              value={newTitle}
              onChange={setNewTitle}
              ariaLabel="Новая задача в «Мой день»"
              schedule={{
                dueOn: newDueOn,
                onDueChange: setNewDueOn,
                recurrence: newRecurrence,
                onRecurrenceChange: setNewRecurrence,
              }}
            />
          </form>
        </div>
      )}

      <div className="px-5 md:px-7 flex-1 flex flex-col">

        {visiblePending.length > 0 && (
          <SortableContext items={boardIds} strategy={boardStrategy}>
            <div
              className={`-mx-2 md:-mx-4 space-y-1.5 ${inboxId ? "mt-1.5" : ""}`}
              onClick={(e) => {
                if (e.target === e.currentTarget) selection.clear();
              }}
            >
              {hasInOrder && (
                <MyDayBlockHeader
                  sequential={false}
                  emptyHint={anyTimeRows.length === 0 ? "Перетащи сюда то, что можно сделать когда угодно" : undefined}
                  collapsed={collapsedBlocks.anyTime}
                  onToggleCollapsed={anyTimeRows.length > 0 ? () => toggleBlock("anyTime") : undefined}
                />
              )}
              {shownAnyTimeRows.map((task) => renderTaskTree(task, 0, true))}
              {(hasInOrder || rootDragActive) && (
                <MyDayBlockHeader
                  sequential
                  emptyHint={hasInOrder ? undefined : "Перетащи сюда задачи, которые делаются строго друг за другом"}
                  zenHref={hasInOrder ? "/zen?my_day=in_order" : undefined}
                  collapsed={collapsedBlocks.inOrder}
                  onToggleCollapsed={hasInOrder ? () => toggleBlock("inOrder") : undefined}
                  between={{ rowIds: boardIds, boundary: blockBoundary }}
                />
              )}
              {shownInOrderRows.map((task) => renderTaskTree(task, 0, true))}
            </div>
          </SortableContext>
        )}

        <HiddenTasksToggle count={hiddenCount} revealed={revealed} onToggle={toggleRevealed} />

        {visiblePending.length === 0 && hiddenCount === 0 && (
          <EmptyState
            icon={SUN_EMPTY}
            text={
              !local && isOfflineStandIn(props.fetchedAt)
                ? OFFLINE_EMPTY_TEXT
                : allDone
                ? "День закрыт ✨ Отличная работа."
                : "На сегодня всё чисто ✨"
            }
          />
        )}

        {completedTasksExist && (
          <div className={orderedPending.length === 0 ? "mt-auto pb-6" : "mt-3"}>
            <button
              type="button"
              onClick={completed.toggle}
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer flex items-center gap-1"
            >
              <svg
                className={`w-3 h-3 transition-transform ${completed.open ? "rotate-90" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              {completed.loading ? "Загрузка…" : "Завершённые"}
            </button>
            {completed.open && cachedCompleted && cachedCompleted.length > 0 && (
              <div className="-mx-2 md:-mx-4 mt-2 space-y-1.5 opacity-80">
                {cachedCompleted.map((task) => renderTaskTree(task, 0, false))}
              </div>
            )}
          </div>
        )}
      </div>
      </div>

      {taskPendingDelete && (
        <DeleteConfirm
          title={`Удалить «${taskPendingDelete.title}»?`}
          body="Задача будет удалена безвозвратно."
          onConfirm={confirmDeleteTask}
          onCancel={() => setTaskPendingDelete(null)}
        />
      )}

      {selectedTask && (
        <DetailsPanel
          task={selectedTask}
          lists={listOptions}
          onClose={() => setSelectedTask(null)}
          onDelete={(t) => {
            setSelectedTask(null);
            setTaskPendingDelete(t);
          }}
          onTreeMutated={refreshExpandedSubtrees}
        />
      )}

      {bulkDeletePending && (
        <DeleteConfirm
          title={
            bulkDeletePending.length === 1
              ? `Удалить «${bulkDeletePending[0].title}»?`
              : `Удалить ${taskCount(bulkDeletePending.length)}?`
          }
          body={
            bulkDeletePending.length === 1
              ? "Задача будет удалена безвозвратно."
              : "Задачи будут удалены безвозвратно."
          }
          onConfirm={confirmBulkDelete}
          onCancel={() => setBulkDeletePending(null)}
        />
      )}

      {contextMenu && (
        <TaskContextMenu
          tasks={contextMenu.tasks}
          lists={listOptions}
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onPatch={applyMenuPatch}
          onDelete={() => setBulkDeletePending(contextMenu.tasks)}
          onMoveToMyDayBlock={
            contextMenu.tasks.length === 1 && orderedPending.some((t) => t.id === contextMenu.tasks[0].id)
              ? (sequential) => {
                  moveRootTask(contextMenu.tasks[0].id, "last", sequential);
                  selection.clear();
                }
              : undefined
          }
        />
      )}

      <MyDayPlanModal />
    </AppShell>
  );
}
